import { formatRate } from './counters-format'

/**
 * THE DOCUMENTATION AUDIT — shape, wire coercion and every word (Build 10c).
 *
 * WHAT IT IS FOR. Kelvo's value is not "chat with your documents"; it is
 * finding what a firm has NOT written down. Over any run of days the owner
 * picks, this says how many questions staff asked, how many the written-down
 * answers covered, how many were not written down, how many of those were
 * about keeping people safe, and where each of those stands now. It is the
 * sales artefact — "first fortnight: 23 questions, 9 not written down, 3 about
 * safety" — and the owner's own to-do list.
 *
 * DERIVED, NEVER STORED (workers/audit.ts). Asks and gaps are counted from the
 * event log over the days chosen; where each gap stands now is read from the
 * question itself; whether it was about safety is read from its words at the
 * moment the report is opened (workers/safety-topic.ts). Nothing here is kept.
 *
 * DEPENDENCY-FREE, like counters-format.ts and digest-format.ts, because both
 * lanes import it: the engine sends this shape, the page coerces and words it,
 * the CSV is built from the same rows the page shows.
 *
 * WHAT IT MUST NEVER SAY:
 *   - "0%" when nobody asked anything;
 *   - "100%" when one question went unanswered (floored, by formatRate);
 *   - a count for days before the firm's records began, as if it were a zero;
 *   - "about safety" for a question whose words never said so.
 */

/** Where the gaps opened in the window stand now. */
export interface GapCohort {
  opened: number
  answered: number
  dismissed: number
  /** Still waiting for the owner. */
  waiting: number
}

export interface GapAudit {
  /** The days covered, UK calendar days, inclusive. */
  fromDay: string
  toDay: string
  /** The first day this firm has any question on record, or null if none.
   *  Days before it were not counted, which is not the same as nobody asking. */
  recordsFrom: string | null
  /** Staff questions in the window, and how many the written-down answers
   *  covered (question_asked / question_answered). */
  asked: number
  answered: number
  /** Every gap opened in the window — a question that was not written down. */
  gaps: GapCohort
  /** The subset whose question named one of the firm's safety words. Null
   *  when the firm has no safety words set, so no split is claimed. */
  safety: GapCohort | null
  /** Gaps whose question can no longer be read (none, unless the firm's
   *  questions were erased). In `gaps.opened`, in no other count. */
  unreadable: number
  /** The words a question had to name to count as about safety. */
  safetyTerms: string[]
  /** True when there were more gaps than one report reads (AUDIT_MAX_GAPS);
   *  the counts then cover the first of them only, and the page says so. */
  capped: boolean
}

/* ---------------------------------------------------------------------------
 * Never trust the wire.
 * ------------------------------------------------------------------------- */

const DAY = /^\d{4}-\d{2}-\d{2}$/

function count(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) && v > 0 ? Math.floor(v) : 0
}

function cohort(v: unknown): GapCohort {
  const r = (v ?? {}) as Record<string, unknown>
  return { opened: count(r.opened), answered: count(r.answered), dismissed: count(r.dismissed), waiting: count(r.waiting) }
}

/** A garbled response becomes an empty report for no days — never invented
 *  numbers. A garbled safety block becomes null: no split claimed. */
export function coerceGapAudit(raw: unknown): GapAudit {
  const r = (raw ?? {}) as Record<string, unknown>
  const day = (v: unknown) => (typeof v === 'string' && DAY.test(v) ? v : '')
  return {
    fromDay: day(r.fromDay),
    toDay: day(r.toDay),
    recordsFrom: typeof r.recordsFrom === 'string' && DAY.test(r.recordsFrom) ? r.recordsFrom : null,
    asked: count(r.asked),
    answered: count(r.answered),
    gaps: cohort(r.gaps),
    safety: r.safety && typeof r.safety === 'object' ? cohort(r.safety) : null,
    unreadable: count(r.unreadable),
    safetyTerms: Array.isArray(r.safetyTerms) ? r.safetyTerms.filter((t): t is string => typeof t === 'string') : [],
    // A garbled flag reads as cut short: the warning costs a sentence; its
    // absence would claim a complete count that may not be.
    capped: r.capped !== false,
  }
}

/* ---------------------------------------------------------------------------
 * Words.
 * ------------------------------------------------------------------------- */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** "6 Oct 2026" from "2026-10-06". Built by hand: ASCII, the same on every
 *  runtime and in every reader's timezone. */
export function formatAuditDay(day: string): string {
  if (!DAY.test(day)) return ''
  const [y, m, d] = day.split('-').map(Number) as [number, number, number]
  return `${d} ${MONTHS[m - 1] ?? ''} ${y}`
}

/** "6 Oct to 19 Oct 2026", or "6 Oct 2026" for one day, or the two years. */
export function formatDayRange(fromDay: string, toDay: string): string {
  if (!DAY.test(fromDay) || !DAY.test(toDay)) return ''
  if (fromDay === toDay) return formatAuditDay(fromDay)
  const sameYear = fromDay.slice(0, 4) === toDay.slice(0, 4)
  const from = sameYear ? formatAuditDay(fromDay).replace(/ \d{4}$/, '') : formatAuditDay(fromDay)
  return `${from} to ${formatAuditDay(toDay)}`
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`
}

/** The note when the window starts before the firm's records do, else null. */
export function recordsNote(a: GapAudit): string | null {
  if (a.recordsFrom === null) {
    return 'There are no questions on record for these days, so there is nothing to count yet.'
  }
  if (a.fromDay && a.fromDay < a.recordsFrom) {
    return `Records start on ${formatAuditDay(a.recordsFrom)}. Days before that are not counted, which is not the same as nobody asking.`
  }
  return null
}

/** Said when the report read only part of a very long run of gaps. */
export const CAPPED_NOTE = 'There were too many to count in one report. Pick fewer days.'

/**
 * The one sentence to take to a meeting. Every number is the page's own.
 *   "6 Oct to 19 Oct 2026: 23 questions asked, 14 answered from what is
 *    written down (60%). 9 were not written down, 3 of them about safety.
 *    Since then: 6 answered, 1 dismissed, 2 still waiting."
 */
export function summariseAudit(a: GapAudit): string {
  const when = formatDayRange(a.fromDay, a.toDay)
  if (a.asked === 0 && a.gaps.opened === 0) return `${when}: no questions asked.`

  const parts: string[] = []
  if (a.asked === 0) {
    parts.push('no questions counted as asked')
  } else {
    const answered = Math.min(a.answered, a.asked)
    parts.push(
      `${plural(a.asked, 'question', 'questions')} asked, ${answered} answered from what is written down (${formatRate(answered / a.asked)})`,
    )
  }
  let sentence = `${when}: ${parts.join('')}.`

  if (a.gaps.opened === 0) {
    sentence += ' Everything asked was written down.'
    return sentence
  }
  const gapWord = a.gaps.opened === 1 ? '1 was not written down' : `${a.gaps.opened} were not written down`
  const safety =
    a.safety === null
      ? ''
      : a.safety.opened === 0
        ? ', none of them about safety'
        : `, ${a.safety.opened} of them about safety`
  sentence += ` ${gapWord}${safety}.`

  const since: string[] = []
  if (a.gaps.answered > 0) since.push(`${a.gaps.answered} answered`)
  if (a.gaps.dismissed > 0) since.push(`${a.gaps.dismissed} dismissed`)
  if (a.gaps.waiting > 0) since.push(`${a.gaps.waiting} still waiting`)
  if (since.length > 0) sentence += ` Since then: ${since.join(', ')}.`
  return sentence
}

/** One labelled number: what the table shows and the CSV holds. */
export interface AuditRow {
  label: string
  value: number | string
}

/**
 * The rows of the report, in order. The page's table and the CSV are both
 * built from this, so the two cannot disagree. The rate is words, not a
 * number, so "none asked" can never be exported as 0%.
 */
export function auditRows(a: GapAudit): AuditRow[] {
  const rate = a.asked === 0 ? 'none asked' : formatRate(Math.min(a.answered, a.asked) / a.asked)
  const rows: AuditRow[] = [
    { label: 'Questions asked', value: a.asked },
    { label: 'Answered from what is written down', value: Math.min(a.answered, a.asked) },
    { label: 'Answer rate', value: rate },
    { label: 'Not written down', value: a.gaps.opened },
  ]
  if (a.safety !== null) rows.push({ label: 'Not written down, about safety', value: a.safety.opened })
  rows.push(
    { label: 'Since answered', value: a.gaps.answered },
    { label: 'Since dismissed', value: a.gaps.dismissed },
    { label: 'Still waiting', value: a.gaps.waiting },
  )
  if (a.safety !== null && a.safety.opened > 0) {
    rows.push({ label: 'About safety, still waiting', value: a.safety.waiting })
  }
  if (a.unreadable > 0) rows.push({ label: 'Could not be read', value: a.unreadable })
  return rows
}

/**
 * The CSV a spreadsheet opens. Every label is one of ours and every value a
 * number or one of our words, but each cell is still quoted and any leading
 * formula character neutralised (OWASP CSV injection): this file is opened in
 * Excel by someone else, and the rule costs nothing to keep.
 */
export function auditCsv(a: GapAudit): string {
  const cell = (v: number | string) => {
    const text = String(v)
    const safe = /^[=+\-@\t\r]/.test(text) ? `'${text}` : text
    return `"${safe.replace(/"/g, '""')}"`
  }
  const lines = [
    [cell('From'), cell(a.fromDay)].join(','),
    [cell('To'), cell(a.toDay)].join(','),
    ...auditRows(a).map((r) => [cell(r.label), cell(r.value)].join(',')),
  ]
  return `${lines.join('\r\n')}\r\n`
}

/** The CSV's file name: kelvo-not-written-down-2026-10-06-to-2026-10-19.csv */
export function auditCsvName(a: GapAudit): string {
  return `kelvo-not-written-down-${a.fromDay}-to-${a.toDay}.csv`
}
